import {
  handleSort,
  SortDirection,
} from "@transfr-inc/dashboard-components/data-table";
import { useEffect, useState } from "react";

import { ClassroomGradebookFilter } from "@components/custom/forms/classroom-gradebook.filter";
import { useApiRequest } from "@lib/hooks";
import container from "@src/container";
import { ClassroomGradebookTable } from "@views/classrooms/details/components/classroom.gradebook-table";
import {
  createModulesMap,
  mergeMasteryInfoGradebook,
} from "@views/classrooms/details/components/classroom.modules.utils";

import "./train.gradebook.scss";

export const TrainGradebook = ({ classroom, users, readOnly }) => {
  const [gradebookData, setGradebookData] = useState();
  const [moduleData, setModuleData] = useState();
  const [filters, setFilters] = useState({});
  const [defaultModule, setDefaultModule] = useState();

  const { insightsService } = container;

  const { loading: loadingModules, response: modulesMap } = useApiRequest(() =>
    insightsService
      .getModulesForOrg(classroom.clientCode, false, classroom.classroomId)
      .then(createModulesMap)
  );

  const {
    loading: loadingMasteryInfo,
    response: masteryInfo,
    sendRequest: refetchMasteryInfo,
  } = useApiRequest(() =>
    insightsService
      .getClassroomMasteryInfo(classroom.classroomId)
      .catch(() => new Map())
  );

  const onSortClick = ({ column, sortDirection }) => {
    const sortedData = handleSort(
      moduleData,
      column,
      "isAnonymous",
      sortDirection || SortDirection.ASCENDING
    );
    setModuleData(sortedData);
  };

  const onApplyFilters = (filtersData) => {
    setFilters(filtersData);
  };

  useEffect(() => {
    if (modulesMap && masteryInfo) {
      const modulesArray = [...modulesMap.values()];
      const result = mergeMasteryInfoGradebook(
        users,
        modulesArray,
        masteryInfo
      );

      setGradebookData(result);
      // Keep the initial default module stable so a refetch doesn't reset the
      // instructor's current filter selection.
      setDefaultModule((prev) => prev ?? result.get(modulesArray[0].moduleId));
    }
  }, [modulesMap, masteryInfo]);

  useEffect(() => {
    if (!gradebookData) return;

    const moduleId = filters?.module?.moduleId;
    const freshModuleUsers = moduleId
      ? gradebookData.get(moduleId)?.users
      : undefined;

    // Re-derive the visible rows from the latest gradebook data (e.g. after a
    // score override refetch) while preserving the current user selection.
    if (freshModuleUsers && filters?.users) {
      const selectedIds = new Set(filters.users.map((u) => u.userId));
      setModuleData(freshModuleUsers.filter((u) => selectedIds.has(u.userId)));
    } else {
      setModuleData(filters.users);
    }
  }, [gradebookData, filters]);

  useEffect(() => () => insightsService.cancelRequest(), []);

  return (
    <div className="classroom-gradebook-tab">
      <div className="header-container">
        <div className="gradebook-filter-container">
          <ClassroomGradebookFilter
            onApplyFilters={onApplyFilters}
            gradebookData={gradebookData}
            defaultModule={defaultModule}
          ></ClassroomGradebookFilter>
          {filters.module && (
            <div className="filter-module">
              Module: <span>{filters?.module?.displayName}</span>
            </div>
          )}
        </div>
      </div>
      <div className="gradebook-table-container">
        <ClassroomGradebookTable
          data={moduleData}
          simulations={filters.simulations}
          classroom={classroom}
          isLoading={loadingMasteryInfo || loadingModules}
          onSortClick={onSortClick}
          onScoreUpdated={refetchMasteryInfo}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
};
